import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import { BodyM } from 'components/Typography/Typography';
import styles from 'assets/jss/material-dashboard-pro-react/components/customRepeatEveryStyle';
import { translate } from 'components/Translation';
import CustomInput from 'components/CustomInput/CustomInput';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import moment from 'moment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import DateRow from './subcomponents/DateRow';
import CustomRepeatEverySummary from './subcomponents/CustomRepeatEverySummary';

// @ts-ignore
const useStyles = makeStyles(styles);

export enum PeriodUnit {
	DAY = 'DAY',
	WEEK = 'WEEK',
	MONTH = 'MONTH',
	YEAR = 'YEAR',
}
enum DateType {
	START_DATE = 'startDate',
	END_DATE = 'endDate',
}

interface ICustomRepeatEveryProps {
	allowedPeriod: PeriodUnit[];
	isSummary?: boolean;
	selectedPeriod: PeriodUnit;
	setSelectedPeriod: React.Dispatch<React.SetStateAction<PeriodUnit>>;
	periodicity: string;
	setPeriodicity: React.Dispatch<React.SetStateAction<string>>;
	period: Period;
	setPeriod: React.Dispatch<React.SetStateAction<Period>>;
	numberOfOrders: number;
	repeatPeriod: number;
	setRepeatPeriod: React.Dispatch<React.SetStateAction<number>>;
}

export interface Period {
	startDate: moment.Moment;
	endDate: moment.Moment | null;
}

const CustomRepeatEvery = ({
	allowedPeriod,
	isSummary,
	selectedPeriod,
	setSelectedPeriod,
	periodicity,
	setPeriodicity,
	period,
	setPeriod,
	numberOfOrders,
	repeatPeriod,
	setRepeatPeriod,
}: ICustomRepeatEveryProps) => {
	const classes = useStyles();
	useEffect(()=>{
		setRepeatPeriod(1)
	},[selectedPeriod])
	useEffect(() => {
		setPeriod((period) => ({
			...period,
			endDate: moment(period.startDate).add(
				Number(repeatPeriod),
				(selectedPeriod as unknown) as moment.unitOfTime.Diff
			),
		}));
	}, [selectedPeriod, repeatPeriod]);
	const validateDateString = (date: string): boolean => {
		return /^([0][1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]\d{4}$/.test(date);
	};

	const dateChanged = (date: string | moment.Moment, key: DateType.START_DATE | DateType.END_DATE): void => {
		if (typeof date === 'string') {
			if (validateDateString(date)) {
				const updatedDate = moment(date);
				if (updatedDate.isValid()) {
					setPeriod((period) => ({ ...period, [key]: updatedDate }));
				}
			}
		} else {
			setPeriod((period) => ({ ...period, [key]: date }));
		}
	};

	const startDateValid = (date: moment.Moment): boolean => {
		return !period.endDate || date.isSameOrBefore(period.endDate, 'day');
	};

	const endDateValid = (date: moment.Moment): boolean => {
		return !period.startDate || date.isSameOrAfter(period.startDate, 'day');
	};

	return (
		<div className={classes.repeatEveryWrapper}>
			<div className={classes.repeatEveryPeriodicityWrapper}>
				<div className={classes.labelRepeatEveryWrapper}>
					<BodyM disableMargin color="primary-light">
						{translate('custom.repeat.every')}
					</BodyM>
				</div>
				<div className={classes.periodicityInput}>
					<CustomInput
						id="periodicity-input"
						formControlProps={{ fullWidth: true }}
						error={Number(periodicity) <= 0}
						inputProps={{
							required: true,
							value: periodicity,
							inputProps: { min: 1, max: 99 },
							onChange: ({ target: { value } }: { target: HTMLInputElement }) => setPeriodicity(value),
							type: 'number',
						}}
						hideArrows={false}
					/>
				</div>
				<div className={classes.periodSelect}>
					<AlternativeSelect
						id="period-select"
						value={
							<div className={classes.selectedItem}>
								<BodyM color="primary-light" disableMargin>
									{translate(`custom.repeat.every.${selectedPeriod.toLowerCase()}`)}
								</BodyM>
							</div>
						}
						onClick={(item) => {
							setSelectedPeriod(item);
						}}
						items={allowedPeriod}
						selectedItems={selectedPeriod ? [selectedPeriod] : []}
						withHorizontalPadding={true}
					/>
				</div>
			</div>

			<div className={classes.repeatEveryDatesWrapper}>
				<DateRow
					dateValue={period.startDate}
					isValidDate={startDateValid}
					dateChanged={(date) => dateChanged(date, DateType.START_DATE)}
					label={translate(`custom.repeat.every.${DateType.START_DATE}`)}
				/>
				<ConditionalRender
					show={[PeriodUnit.WEEK, PeriodUnit.MONTH].indexOf(selectedPeriod) >= 0}
					onTrue={() => (
						<div className={classes.repeatPeriod}>
							<div className={classes.labelRepeatPeriodWrapper}>
								<BodyM disableMargin color="primary-light">
									{translate(`custom.repeat.period.${selectedPeriod.toLowerCase()}`)}
								</BodyM>
							</div>
							<div className={classes.repeatPeriodInput}>
								<CustomInput
									id="repeat-period-input"
									error={repeatPeriod <= 0}
									formControlProps={{ fullWidth: true }}
									inputProps={{
										required: false,
										value: repeatPeriod || '',
										inputProps: { min: 0 },
										onChange: ({ target: { value } }: { target: HTMLInputElement }) => {
											setRepeatPeriod(Number(value));
											setPeriod((period) => ({
												...period,
												endDate: moment(period.startDate).add(
													Number(value),
													(selectedPeriod as unknown) as moment.unitOfTime.Diff
												),
											}));
										},
										type: 'number',
									}}
									hideArrows={false}
								/>
							</div>
						</div>
					)}
				/>
				<DateRow
					readOnly={[PeriodUnit.WEEK, PeriodUnit.MONTH].indexOf(selectedPeriod) >= 0}
					dateValue={period.endDate}
					isValidDate={endDateValid}
					dateChanged={(date) => dateChanged(date, DateType.END_DATE)}
					label={translate(`custom.repeat.every.${DateType.END_DATE}`)}
					placeholder={translate('custom.repeat.every.chooseDate')}
				/>
			</div>
			<div>
				<ConditionalRender
					show={!!period.endDate && Number(periodicity) >= 1 && !!isSummary}
					onTrue={() => (
						<CustomRepeatEverySummary
							numberOfOrders={numberOfOrders}
							periodUnit={selectedPeriod}
							periodicity={periodicity}
							startDate={period.startDate.format('DD/MM/YYYY')}
							endDate={period.endDate ? period.endDate.format('DD/MM/YYYY') : ''}
						/>
					)}
				/>
			</div>
		</div>
	);
};

export default CustomRepeatEvery;
